import React, { useState, useEffect, useRef } from 'react';
import { pdfAPI } from '../utils/api';
import { CustomContent, ServiceLine } from '../types';

interface EditTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceLine: ServiceLine;
  onSave: () => void;
}

const EditTemplateModal: React.FC<EditTemplateModalProps> = ({
  isOpen,
  onClose,
  serviceLine,
  onSave
}) => {
  const [content, setContent] = useState<CustomContent>({
    title: '',
    description: '',
    sectionTitle: 'OUR FOCUS, YOUR DIAGNOSIS.',
    page2Content: {
      section1: {
        title: 'LEADERS IN DERMATOPATHOLOGY',
        description: 'We pride ourselves on our team of board-certified doctors who have extensive training in dermatology and dermatopathology',
        enabled: true
      },
      section2: {
        title: 'COMPLETELY PHYSICIAN-OWNED',
        description: 'We\'re not a publicly traded company. No outside investors.',
        enabled: true
      },
      section3: {
        title: 'DEDICATED IN-HOUSE BILLING',
        description: 'Same support team year-round for patients and providers alike.',
        enabled: true
      },
      section4: {
        title: 'COMMITTED COURIER SERVICE',
        description: 'Know who\'s servicing your location everyday.',
        enabled: true
      },
      section5: {
        title: 'RAPID TURN-AROUND-TIME',
        description: 'Comprehensive testing (including DIF studies) performed in-house for quick results.',
        enabled: true
      }
    }
  });
  const [activeTab, setActiveTab] = useState<'page1' | 'page2'>('page1');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchTemplateContent();
    }
  }, [isOpen, serviceLine]);

  const fetchTemplateContent = async () => {
    try {
      setLoading(true);
      const templateContent = await pdfAPI.getTemplateContent(serviceLine);
      setContent(prev => ({
        ...prev,
        ...templateContent
      }));
    } catch (err) {
      setError('Failed to load template content');
      console.error('Error fetching template content:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CustomContent, value: string) => {
    setContent(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePage2InputChange = (section: keyof NonNullable<CustomContent['page2Content']>, field: 'title' | 'description', value: string) => {
    setContent(prev => ({
      ...prev,
      page2Content: {
        ...prev.page2Content!,
        [section]: {
          ...prev.page2Content![section],
          [field]: value
        }
      }
    }));
  };

  const handlePage2ToggleEnabled = (section: keyof NonNullable<CustomContent['page2Content']>) => {
    setContent(prev => ({
      ...prev,
      page2Content: {
        ...prev.page2Content!,
        [section]: {
          ...prev.page2Content![section],
          enabled: !prev.page2Content![section].enabled
        }
      }
    }));
  };

  const getEnabledSectionsCount = () => {
    if (!content.page2Content) return 0;
    return Object.values(content.page2Content).filter(section => section.enabled).length;
  };

  const handleSave = async () => {
    if (!content.title?.trim() || !content.description?.trim() || !content.sectionTitle?.trim()) {
      setError('Page 1 title, description, and section title are required');
      return;
    }

    const enabledCount = getEnabledSectionsCount();
    if (enabledCount === 0) {
      setError('At least one section must be enabled on Page 2');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      await pdfAPI.updateTemplateContent(content, serviceLine);
      setMessage('Template content updated successfully!');
      
      // Scroll to top after save
      modalBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Call onSave callback to refresh parent component
      onSave();
      
      // Close modal after successful save
      setTimeout(() => {
        setMessage(null);
        onClose();
      }, 2000);
    } catch (err) {
      setError('Failed to update template content');
      console.error('Error updating template content:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    fetchTemplateContent();
    setError(null);
    setMessage(null);
  };

  {/*const handleResetToDefault = async () => {
    if (!window.confirm('Are you sure you want to reset to default content? This will discard all custom changes.')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/template-content/${serviceLine}/reset`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to reset template content');
      }
      
      const result = await response.json();
      
      // Update local state with default content
      setContent(result.content);
      setMessage('Template content reset to default successfully!');
      
      
      // Scroll to top after reset
      modalBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      // Call onSave callback to refresh parent component
      onSave();
      
      setTimeout(() => {
        setMessage(null);
        onClose();
      }, 2000);
    } catch (err) {
      setError('Failed to reset template content');
      console.error('Error resetting template content:', err);
    } finally {
      setSaving(false);
    }
  };*/}

  const getServiceLineDisplayName = (sl: ServiceLine) => {
    const names = {
      dermatology: 'Dermatology',
      podiatry: 'Podiatry', 
      surgical: 'Surgical'
    };
    return names[sl];
  };

  if (!isOpen) return null;

  const renderPage1Editor = () => (
    <>
      {/* Title Section */}
      <div className="mb-4">
        <label htmlFor="title" className="form-label">
          <strong>Page Title</strong>
        </label>
        <div className="mb-2">
          <small className="text-muted">
            This appears as the main title on the first page of the PDF. 
            Example: "DERMATOPATHOLOGISTS", "PODIATRISTS", etc.
          </small>
        </div>
        <input
          type="text"
          className="form-control"
          id="title"
          value={content.title || ''}
          onChange={(e) => handleInputChange('title', e.target.value)}
          placeholder="Enter the title text..."
          maxLength={50}
        />
        <div className="form-text">
          {content.title?.length || 0}/50 characters
        </div>
      </div>

      {/* Section Title */}
      <div className="mb-4">
        <label htmlFor="sectionTitle" className="form-label">
          <strong>Section Title</strong>
        </label>
        <div className="mb-2">
          <small className="text-muted">
            This is the title for the "Our Focus, Your Diagnosis" section that appears on both pages.
            Keep it short and impactful.
          </small>
        </div>
        <input
          type="text"
          className="form-control"
          id="sectionTitle"
          value={content.sectionTitle || ''}
          onChange={(e) => handleInputChange('sectionTitle', e.target.value)}
          placeholder="Enter the section title..."
          maxLength={100}
        />
        <div className="form-text">
          {content.sectionTitle?.length || 0}/100 characters
        </div>
      </div>

      {/* Description Section */}
      <div className="mb-4">
        <label htmlFor="description" className="form-label">
          <strong>Description Paragraph</strong>
        </label>
        <div className="mb-2">
          <small className="text-muted">
            This appears below the section title. 
            Describe Sagis's commitment to quality diagnostic services.
          </small>
        </div>
        <textarea
          className="form-control"
          id="description"
          rows={5}
          value={content.description || ''}
          onChange={(e) => handleInputChange('description', e.target.value)}
          placeholder="Enter the description paragraph..."
          maxLength={500}
        />
        <div className="form-text">
          {content.description?.length || 0}/500 characters
        </div>
      </div>
    </>
  );

  const renderPage2Editor = () => (
    <>
      <div className="alert alert-info mb-4">
        <strong>Page 2 Features</strong><br />
        <small>
          Enable/disable sections as needed. Each section shows with a checkmark icon.
          At least one section must be enabled. Disabled sections won't appear in the PDF.
        </small>
      </div>

      <div className="mb-3">
        <span className="badge bg-primary me-2">
          {getEnabledSectionsCount()}/5 sections enabled
        </span>
        <span className="text-muted">
          {getEnabledSectionsCount() === 0 ? 'At least one section must be enabled!' : ''}
        </span>
      </div>

      {content.page2Content && Object.entries(content.page2Content).map(([sectionKey, sectionData]) => (
        <div key={sectionKey} className="card mb-3 sectioninfo-box-edittemplate">
          <div className="card-header d-flex align-items-center">
            <div className="form-check me-3">
              <input
                className="form-check-input"
                type="checkbox"
                checked={sectionData.enabled}
                onChange={() => handlePage2ToggleEnabled(sectionKey as keyof NonNullable<CustomContent['page2Content']>)}
                id={`${sectionKey}-enabled`}
              />
              <label className="form-check-label" htmlFor={`${sectionKey}-enabled`}>
                <strong>Section {sectionKey.slice(-1)} {sectionData.enabled ? '(Enabled)' : '(Disabled)'}</strong>
              </label>
            </div>
          </div>
          
          <div className="card-body">
            {/* Title */}
            <div className="mb-3">
              <label className="form-label">
                <strong>Section Title</strong>
              </label>
              <input
                type="text"
                className={`form-control ${!sectionData.enabled ? 'bg-light' : ''}`}
                value={sectionData.title}
                onChange={(e) => handlePage2InputChange(
                  sectionKey as keyof NonNullable<CustomContent['page2Content']>, 
                  'title', 
                  e.target.value
                )}
                placeholder="Enter section title..."
                maxLength={100}
                disabled={!sectionData.enabled}
              />
              <div className="form-text">
                {sectionData.title?.length || 0}/100 characters
              </div>
            </div>

            {/* Description */}
            <div className="mb-3">
              <label className="form-label">
                <strong>Section Description</strong>
              </label>
              <textarea
                className={`form-control ${!sectionData.enabled ? 'bg-light' : ''}`}
                rows={3}
                value={sectionData.description}
                onChange={(e) => handlePage2InputChange(
                  sectionKey as keyof NonNullable<CustomContent['page2Content']>, 
                  'description', 
                  e.target.value
                )}
                placeholder="Enter section description..."
                maxLength={300}
                disabled={!sectionData.enabled}
              />
              <div className="form-text">
                {sectionData.description?.length || 0}/300 characters
              </div>
            </div>

            {/* Preview */}
            <div className={`border rounded p-2 ${sectionData.enabled ? '' : 'bg-light'}`} style={{ backgroundColor: sectionData.enabled ? '#f8f9fa' : '#f8f9fa', opacity: sectionData.enabled ? 1 : 0.6 }}>
              <div className="d-flex align-items-start">
                <span className={`me-2 ${sectionData.enabled ? 'text-success' : 'text-muted'}`} style={{ fontSize: '1.2em' }}>
                  {sectionData.enabled ? '✓' : '⭕'}
                </span>
                <div>
                  <h6 className="mb-1" style={{ fontWeight: 'bold' }}>
                    {sectionData.title || '[SECTION TITLE]'}
                    {!sectionData.enabled && <span className="text-muted"> (Hidden in PDF)</span>}
                  </h6>
                  <p className="small text-muted mb-0">
                    {sectionData.description || '[SECTION DESCRIPTION]'}
                  </p>
                </div>
              </div>
            </div>

            {!sectionData.enabled && (
              <div className="alert alert-warning mt-2 mb-0">
                <small><i className="bi bi-exclamation-triangle me-1"></i>This section is disabled and will not appear in the PDF.</small>
              </div>
            )}
          </div>
        </div>
      ))}
    </>
  );

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-xl">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              ✏️ Edit Template Content - {getServiceLineDisplayName(serviceLine)}
            </h5>
            <button 
              type="button" 
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>
          
          <div className="modal-body" ref={modalBodyRef} style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {loading ? (
              <div className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
                <div className="spinner-border text-success" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                {/* Messages */}
                {message && (
                  <div className="alert alert-success alert-dismissible fade show" role="alert">
                    {message}
                    <button 
                      type="button" 
                      className="btn-close"
                      onClick={() => setMessage(null)}
                    ></button>
                  </div>
                )}

                {error && (
                  <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button 
                      type="button" 
                      className="btn-close"
                      onClick={() => setError(null)}
                    ></button>
                  </div>
                )}

                {/* Tab Navigation */}
                <ul className="nav nav-tabs mb-4" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button 
                      className={`nav-link ${activeTab === 'page1' ? 'active' : ''}`}
                      onClick={() => setActiveTab('page1')}
                      type="button"
                    >
                      📄 Page 1 Content
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button 
                      className={`nav-link ${activeTab === 'page2' ? 'active' : ''}`}
                      onClick={() => setActiveTab('page2')}
                      type="button"
                    >
                      📋 Page 2 Features 
                      <span className="badge bg-primary ms-1">{getEnabledSectionsCount()}/5</span>
                    </button>
                  </li>
                </ul>
                
                {activeTab === 'page1' && (
                  <div>
                    <h6 className="mb-3">First Page Content</h6>
                    <small className="text-muted mb-4 d-block">
                      Edit the main title and description that appear on the first page with pathologist profiles.
                    </small>
                    {renderPage1Editor()}
                  </div>
                )}

                {activeTab === 'page2' && (
                  <div>
                    <h6 className="mb-3">Second Page Features</h6>
                    <small className="text-muted mb-4 d-block">
                      Edit and enable/disable the value propositions that appear on the second page with checkmarks.
                    </small>
                    {renderPage2Editor()}
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="modal-footer">
            {/*<button 
              type="button" 
              className="btn btn-outline-danger"
              onClick={handleResetToDefault}
              disabled={saving || loading}
            >
              Reset to Default
            </button>*/}
            
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            
            <button 
              type="button" 
              className="btn rpg-primary"
              onClick={handleSave}
              disabled={saving || loading || !content.title?.trim() || !content.description?.trim() || !content.sectionTitle?.trim() || getEnabledSectionsCount() === 0}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Saving...
                </>
              ) : (
                'Save All Changes'
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default EditTemplateModal;